import type { UserStateEntry, UserStateItemKey, UserStateMap } from "../../types/user-state";
import { createDefaultUserStateEntry } from "../../types/user-state";
import { removeEntry, setEntry } from "../features/userState/userStateSlice";

export interface UpdateLifecycleArgs {
  itemKey: UserStateItemKey;
  data: Record<string, unknown>;
  version: number;
}

export interface UpdateLifecycleHelpers {
  dispatch: (action: unknown) => any;
  getState: () => any;
  queryFulfilled: Promise<{ data: UserStateEntry }>;
  upsertAll: (entries: UserStateMap) => { undo: () => void };
  upsertItem: (itemKey: UserStateItemKey, entry: UserStateEntry) => { undo: () => void };
}

export async function handleUserStateUpdateLifecycle(
  args: UpdateLifecycleArgs,
  helpers: UpdateLifecycleHelpers
) {
  const { itemKey, data, version } = args;
  const { dispatch, getState, queryFulfilled, upsertAll, upsertItem } = helpers;

  const optimisticEntry: UserStateEntry = {
    data,
    version: version + 1,
    updatedAt: new Date().toISOString(),
  };

  const currentState: any = getState();
  const buildMap = (entry: UserStateEntry) => ({
    ...(currentState?.userState?.entries ?? {}),
    [itemKey]: entry,
  });

  const patches: Array<{ undo: () => void }> = [];

  const patchAll = upsertAll(buildMap(optimisticEntry));
  if (patchAll && typeof (patchAll as any).undo === "function") {
    patches.push(patchAll as { undo: () => void });
  }

  const patchItem = upsertItem(itemKey, optimisticEntry);
  if (patchItem && typeof (patchItem as any).undo === "function") {
    patches.push(patchItem as { undo: () => void });
  }

  const previousEntry = currentState?.userState?.entries?.[itemKey] as UserStateEntry | undefined;
  dispatch(setEntry({ itemKey, entry: optimisticEntry }));

  try {
    const { data: serverEntry } = await queryFulfilled;
    patches.forEach((patch) => patch.undo());
        const refreshedState: any = getState();
        dispatch(setEntry({ itemKey, entry: serverEntry }));
        const patchAllFinal = upsertAll({
          ...(refreshedState?.userState?.entries ?? {}),
          [itemKey]: serverEntry,
        });
        if (patchAllFinal && typeof (patchAllFinal as any).undo === "function") {
          patches.push(patchAllFinal as { undo: () => void });
        }
        const patchItemFinal = upsertItem(itemKey, serverEntry);
        if (patchItemFinal && typeof (patchItemFinal as any).undo === "function") {
          patches.push(patchItemFinal as { undo: () => void });
        }
  } catch (rejected) {
    patches.forEach((patch) => patch.undo());
    const error = rejected as { error?: { status?: number; data?: UserStateEntry } };
    if (error?.error?.status === 409 && error.error.data) {
      const latest = error.error.data;
      dispatch(setEntry({ itemKey, entry: latest }));
      const refreshedState: any = getState();
      const patchAllConflict = upsertAll({
        ...(refreshedState?.userState?.entries ?? {}),
        [itemKey]: latest,
      });
      if (patchAllConflict && typeof (patchAllConflict as any).undo === "function") {
        patches.push(patchAllConflict as { undo: () => void });
      }
      const patchItemConflict = upsertItem(itemKey, latest);
      if (patchItemConflict && typeof (patchItemConflict as any).undo === "function") {
        patches.push(patchItemConflict as { undo: () => void });
      }
      return { conflict: true, latest };
    }

    if (previousEntry) {
      dispatch(setEntry({ itemKey, entry: previousEntry }));
    } else {
      dispatch(setEntry({ itemKey, entry: createDefaultUserStateEntry(itemKey) }));
    }
    return { conflict: false };
  }

  return { conflict: false };
}

export interface DeleteLifecycleArgs {
  itemKey: UserStateItemKey;
  version?: number;
}

export interface DeleteLifecycleHelpers {
  dispatch: (action: unknown) => any;
  getState: () => any;
  queryFulfilled: Promise<unknown>;
  updateAll: (updater: (draft: UserStateMap) => void) => { undo: () => void } | undefined;
  updateItem: (
    itemKey: UserStateItemKey,
    updater: (draft: UserStateEntry) => void
  ) => { undo: () => void } | undefined;
}

export async function handleUserStateDeleteLifecycle(
  args: DeleteLifecycleArgs,
  helpers: DeleteLifecycleHelpers
) {
  const { itemKey } = args;
  const { dispatch, getState, queryFulfilled, updateAll, updateItem } = helpers;

  const currentState: any = getState();
  const previousEntry = currentState?.userState?.entries?.[itemKey] as UserStateEntry | undefined;

  dispatch(removeEntry(itemKey));

  const patches: Array<{ undo: () => void }> = [];
  const patchAll = updateAll((draft) => {
    delete draft[itemKey];
  });
  if (patchAll && typeof (patchAll as any).undo === "function") {
    patches.push(patchAll as { undo: () => void });
  }

  const patchItem = updateItem(itemKey, (draft) => {
    draft.data = {};
    draft.version = 0;
    draft.updatedAt = null;
  });
  if (patchItem && typeof (patchItem as any).undo === "function") {
    patches.push(patchItem as { undo: () => void });
  }

  try {
    await queryFulfilled;
  } catch (rejected) {
    patches.forEach((patch) => patch.undo());
    const error = rejected as { error?: { status?: number; data?: UserStateEntry } };
    if (error?.error?.status === 409 && error.error.data) {
      dispatch(setEntry({ itemKey, entry: error.error.data }));
      return { conflict: true, latest: error.error.data };
    }
    if (previousEntry) {
      dispatch(setEntry({ itemKey, entry: previousEntry }));
    }
    return { conflict: false };
  }

  return { conflict: false };
}

